<template>
    <div class="mt-4">
        <load-and-error :loading="loading" :error="error" :can-retry="canRetry" @retry="refresh()"></load-and-error>

        <div v-if="!loading && !error && part">
            <h1 class="mb-4">{{part.name}}</h1>

            <info-row title="Part Number" icon-class="fa-barcode">
                {{part.part_number}}
            </info-row>

            <info-row title="Description" icon-class="fa-file-text-o">
                {{part.description}}
            </info-row>

            <info-row title="Minimum/Maximum Count" icon-class="fa-arrows-h">
                Minimum: {{part.minimum}}, Maximum: {{part.maximum || 'None'}}
            </info-row>

            <info-row title="Default Storage" icon-class="fa-file-text-o">
                {{part.default_location|locationName}} - {{part.default_storage}}
            </info-row>

            <info-row title="Stock" icon-class="fa-archive">
                <div v-for="(total, location) in part.stock" :key="location">
                    {{location}}: <b>{{total}}</b>
                </div>
            </info-row>

            <info-row title="Average Price" icon-class="fa-money">
                <div v-for="(average, location) in part.average_prices" :key="location">
                    {{location}}: <b>{{average|money}}</b>
                </div>
            </info-row>

            <info-row title="Total Cost" icon-class="fa-money">
                <div v-for="(total, location) in part.total_cost" :key="location">
                    {{location}}: <b>{{total|money}}</b>
                </div>
            </info-row>

            <div class="text-center d-none d-md-block">
                <a :download="part.name.replace('.', '') + '.png'" :href="qrcodeLink">
                  <img :src="qrcodeLink" />
                  <div class="hidden-canvas">
                    <qrcode :value="part.part_number" :options="{ width: 400, margin: 6 }"></qrcode>
                  </div>
                </a>
            </div>

            <div class="row mt-4">
                <div class="col-md-2 mb-4">
                    <router-link
                        :to="{name: 'edit_part', params: {id: part.id}}"
                        class="btn btn-success w-100"
                    >
                        <i class="fa fa-edit"></i> Edit
                    </router-link>
                </div>

                <div class="col-md-2 mb-4">
                    <button class="btn btn-danger w-100" @click="showConfirmArchive()">
                        <i class="fa fa-archive"></i> Archive
                    </button>
                </div>

                <div class="col-md-2 mb-4">
                    <router-link
                        :to="{name: 'add_stock', params: {id: part.id}}"
                        class="btn btn-outline-success w-100"
                    >
                        <i class="fa fa-plus"></i> Add Stock
                    </router-link>
                </div>

                <div class="col-md-2 mb-4">
                    <router-link
                        :to="{name: 'remove_stock', params: {id: part.id}}"
                        class="btn btn-outline-success w-100"
                    >
                        <i class="fa fa-minus"></i> Remove Stock
                    </router-link>
                </div>

                <div class="col-md-2 mb-4">
                    <router-link
                        :to="{name: 'stock', params: {id: part.id}}"
                        class="btn btn-outline-success w-100"
                    >
                        <i class="fa fa-list"></i> Stock History
                    </router-link>
                </div>
            </div>
        </div>

        <b-modal
            ref="confirmArchive"
            title="Confirm Archive"
            ok-variant="danger"
            ok-title="Yes I'm Sure"
            cancel-variant="light"
            @ok="archive"
        >
            <h2>Are you sure you want to archive this part?</h2>
        </b-modal>
    </div>
</template>

<script>
import Vue from 'vue';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import miniToastr from 'mini-toastr';

import api from 'ays-shared/api';
import utils from 'ays-shared/utils';

export default {
  name: 'Part',
  components: { qrcode: VueQrcode },
  data() {
    return {
      loading: false,
      error: '',
      part: null,
      canRetry: true,
      qrcodeLink: '',
    };
  },
  mounted() {
    this.refresh();
  },
  methods: {
    showConfirmArchive() {
      this.$refs.confirmArchive.show();
    },
    async refresh() {
      this.error = '';
      this.loading = true;
      this.canRetry = false;

      try {
        this.part = await api.inventory.get(this.$route.params.id);
      }
      catch (err) {
        this.part = null;
        this.error = utils.errorMessage(err);
        utils.captureException(err, 'Part');
      }
      finally {
        this.loading = false;
      }

      await Vue.nextTick();
      const canvas = document.getElementsByTagName('canvas')[0];
      const context = canvas.getContext('2d');

      context.fillStyle = '#000000';
      context.font = '32px Arial';
      context.fillText(this.part.default_storage, 80, (canvas.height - 40));
      context.fillText(this.part.name, 80, (canvas.height - 10));
      context.save();

      this.qrcodeLink = canvas.toDataURL('image/png');
    },
    async archive() {
      this.part.status = 'archived';

      this.error = '';
      this.loading = true;
      this.canRetry = false;

      this.part.maximum = this.part.maximum || null;

      try {
        miniToastr.success('Archived Part');
        this.$router.push({ name: 'home' });
      }
      catch (err) {
        this.error = utils.errorMessage(err);
        utils.captureException(err, 'Error archiving part', 'Part.archive');
      }
      finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.hidden-canvas {
  display: none;
}
</style>
